import React, { useState, useRef, useEffect } from 'react';
import { Collection } from '../types';

interface CategoryNavProps {
  collections: Collection[];
  selected: string | null;
  onSelect: (handle: string | null) => void;
  showAll?: boolean;
}

export function CategoryNav({ collections, selected, onSelect, showAll = true }: CategoryNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (collections.length <= 1 && !showAll) {
    return null;
  }

  // Get current selection label
  const getSelectedLabel = () => {
    if (selected === null) {
      return 'All Products';
    }
    const collection = collections.find(c => c.handle === selected);
    return collection?.title || 'Products';
  };

  const handleSelect = (handle: string | null) => {
    onSelect(handle);
    setIsOpen(false);
  };

  return (
    <div className="category-dropdown" ref={dropdownRef}>
      <button
        className="category-dropdown-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>Products: {getSelectedLabel()}</span>
        <svg
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="category-dropdown-menu">
          {showAll && collections.length > 1 && (
            <button
              className={`category-dropdown-item ${selected === null ? 'active' : ''}`}
              onClick={() => handleSelect(null)}
            >
              All Products
            </button>
          )}
          {collections.map((collection) => (
            <button
              key={collection.handle}
              className={`category-dropdown-item ${selected === collection.handle ? 'active' : ''}`}
              onClick={() => handleSelect(collection.handle)}
            >
              {collection.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CategoryNav;
